// CampanaAlertas.js — Campana del encabezado (al lado del buscador) con el número de
// alertas sin leer. Al abrirla, un panel con las últimas 15: se marcan como leídas,
// cada una se puede quitar (✕) y «Limpiar» vacía el panel; todo sigue en /alertas.
// El número se refresca al entrar, al volver a la pestaña, cada 2 minutos solo con la pestaña
// a la vista, y al instante cuando una respuesta de avance trae una alerta más nueva (evento
// 'mentoria:alertas' que reparte api.js).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { alertasService } from '../../services/api';
import { tipoDe, hace, esExterno, IcoCampana, IcoPin } from './alertasComun';

const entra = keyframes`from { opacity: 0; transform: translateY(-6px) scale(.98); } to { opacity: 1; transform: none; }`;
const late = keyframes`0%,100% { transform: rotate(0); } 20% { transform: rotate(14deg); } 40% { transform: rotate(-10deg); } 60% { transform: rotate(6deg); } 80% { transform: rotate(-3deg); }`;

const Caja = styled.div`position: relative; display: inline-flex;`;
const BotonSimple = styled.button`
  position: relative; display: grid; place-items: center; width: 42px; height: 42px; padding: 0; border: 0; border-radius: 50%;
  background: transparent; color: ${p => p.$color}; cursor: pointer;
  &:hover, &[aria-expanded='true'] { background: rgba(127,127,127,.16); }
  &:focus-visible { outline: 2px solid ${p => p.$acento}; outline-offset: 2px; }
`;
const Contador = styled.span`
  position: absolute; top: 4px; right: 3px; min-width: 17px; height: 17px; padding: 0 4px; border-radius: 999px;
  background: ${p => p.$acento}; color: #fff; font-size: 10.5px; font-weight: 800; line-height: 17px; text-align: center;
  box-shadow: 0 0 0 2px rgba(10,12,16,.85); pointer-events: none; font-variant-numeric: tabular-nums;
`;
const Agita = styled.span`
  display: inline-grid;
  ${p => p.$on && css`animation: ${late} 900ms ease-in-out 1;`}
  transform-origin: 50% 10%;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Panel = styled.div`
  position: absolute; top: calc(100% + 12px); right: -8px; z-index: 3300; width: min(390px, calc(100vw - 20px));
  border-radius: 16px; overflow: hidden; color: #e8ebef; text-align: left;
  ${p => p.$fuente && `font-family: ${p.$fuente};`}
  background: rgba(16,18,22,.96); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 0 0 1px rgba(255,255,255,.08), 0 24px 60px -18px rgba(0,0,0,.85);
  animation: ${entra} 180ms ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 600px) {
    position: fixed; top: 64px; left: 10px; right: 10px; width: auto; max-height: calc(100vh - 80px); display: flex; flex-direction: column;
  }
  .cab { display: flex; align-items: center; justify-content: space-between; gap: .5rem; padding: .95rem 1rem .7rem; }
  .cab b { font-size: 1.02rem; font-weight: 750; }
  .cab b small { margin-left: .45rem; font-size: .78rem; font-weight: 600; color: ${p => p.$acento}; }
  .lista { max-height: min(460px, 62vh); overflow-y: auto; overscroll-behavior: contain; }
  @media (max-width: 600px) { .lista { max-height: none; flex: 1; } }
  .pie { display: flex; justify-content: space-between; align-items: center; padding: .55rem .6rem; border-top: 1px solid rgba(255,255,255,.08); }
  .vacio { padding: 2.2rem 1.2rem 2.4rem; text-align: center; color: #9aa3ad; font-size: .9rem; }
  .vacio svg { width: 34px; height: 34px; opacity: .45; margin-bottom: .5rem; }
`;
const Texto = styled.button`
  border: 0; background: none; color: ${p => p.$c || '#aeb6bf'}; font: inherit; font-size: .82rem; font-weight: 600; cursor: pointer;
  padding: .4rem .55rem; border-radius: 8px; text-decoration: none;
  &:hover { background: rgba(255,255,255,.07); color: #fff; }
  &:focus-visible { outline: 2px solid ${p => p.$acento || '#fff'}; outline-offset: 1px; }
  &:disabled { opacity: .4; cursor: default; background: none; }
`;
const Item = styled.div`
  position: relative; display: grid; grid-template-columns: 34px 1fr; gap: .75rem; padding: .75rem 2.4rem .75rem 1rem;
  border-top: 1px solid rgba(255,255,255,.05); cursor: ${p => p.$link ? 'pointer' : 'default'};
  background: ${p => p.$nueva ? 'rgba(255,255,255,.045)' : 'transparent'}; transition: background .15s;
  &:hover { background: rgba(255,255,255,.07); }
  .ico { width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center; color: ${p => p.$c};
    background: color-mix(in srgb, ${p => p.$c} 16%, transparent); }
  .tit { font-size: .9rem; font-weight: ${p => p.$nueva ? 700 : 550}; color: ${p => p.$nueva ? '#fff' : '#d3d8de'}; line-height: 1.3; }
  .msg { font-size: .82rem; color: #9aa3ad; line-height: 1.4; margin-top: 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .meta { display: flex; gap: .6rem; align-items: center; margin-top: 5px; font-size: .75rem; color: #7d8792; }
  .meta a, .meta span.ir { color: ${p => p.$acento}; font-weight: 650; text-decoration: none; }
  .punto { position: absolute; left: 6px; top: 50%; width: 6px; height: 6px; margin-top: -3px; border-radius: 50%; background: ${p => p.$acento}; }
  .x { position: absolute; right: .55rem; top: .6rem; width: 26px; height: 26px; border: 0; border-radius: 7px; background: none; color: #7d8792;
    font-size: 1rem; cursor: pointer; opacity: 0; transition: opacity .15s; }
  &:hover .x, .x:focus-visible { opacity: 1; }
  .x:hover { background: rgba(255,255,255,.1); color: #fff; }
  @media (hover: none) { .x { opacity: .7; } }
  .fija { display: inline-flex; align-items: center; gap: 4px; color: #e8ebef; font-weight: 650; }
  ${p => p.$fija && css`
    background: color-mix(in srgb, ${p.$acento} 9%, rgba(255,255,255,.03)); box-shadow: inset 3px 0 0 ${p.$acento};
    &:hover { background: color-mix(in srgb, ${p.$acento} 13%, rgba(255,255,255,.05)); }
  `}
`;

const FondoDestacada = styled.div`
  position: fixed; inset: 0; z-index: 4000; background: rgba(5,7,10,.62); display: grid; place-items: center; padding: 1rem;
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
`;
const VentanaDestacada = styled.div`
  width: min(440px, 100%); border-radius: 20px; padding: 1.6rem 1.5rem 1.3rem; color: #e8ebef; text-align: center;
  background: #15181d; box-shadow: 0 0 0 1px rgba(255,255,255,.08), 0 30px 80px -20px rgba(0,0,0,.9);
  animation: ${entra} 220ms ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  .ico { width: 52px; height: 52px; margin: 0 auto .9rem; border-radius: 16px; display: grid; place-items: center; color: ${p => p.$c};
    background: color-mix(in srgb, ${p => p.$c} 18%, transparent); }
  .ico svg { width: 26px; height: 26px; }
  h2 { margin: 0; font-size: 1.25rem; line-height: 1.3; color: #fff; }
  p { margin: .6rem 0 0; color: #aeb6bf; font-size: .95rem; line-height: 1.5; white-space: pre-wrap; }
  .acc { display: flex; gap: .6rem; justify-content: center; margin-top: 1.3rem; flex-wrap: wrap; }
  button { border: 0; border-radius: 999px; padding: .65rem 1.3rem; font: inherit; font-weight: 700; font-size: .92rem; cursor: pointer; }
  .si { background: ${p => p.$acento}; color: #fff; }
  .no { background: rgba(255,255,255,.08); color: #e8ebef; }
  button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;

const CampanaAlertas = ({ proyectoId, color = '#fff', acento = '#E50914', Boton, botonProps = {} }) => {
  const [abierta, setAbierta] = useState(false);
  const [alertas, setAlertas] = useState(null);
  const [sinLeer, setSinLeer] = useState(0);
  const [agitar, setAgitar] = useState(false);
  const caja = useRef(null);
  const panelRef = useRef(null);
  // En celular el panel va a pantalla completa fuera del encabezado: la cápsula de
  // vidrio (backdrop-filter) atraparía un position: fixed.
  const movil = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;
  const previo = useRef(0);
  const ultima = useRef(0);
  const navigate = useNavigate();

  // Alerta destacada sin leer: ventana al entrar (una vez por sesión de navegador).
  const [destacada, setDestacada] = useState(null);
  const contar = useCallback(() => {
    alertasService.contador(proyectoId).then(r => {
      const d = r.data?.destacada;
      if (d) {
        let vista = false;
        try { vista = sessionStorage.getItem('alerta_destacada_vista') === String(d.id); } catch (e) { /* sin almacenamiento */ }
        if (!vista) setDestacada(d);
      }
      if (r.data?.ultima) ultima.current = r.data.ultima;
      const n = r.data?.sin_leer || 0;
      if (n > previo.current) { setAgitar(true); setTimeout(() => setAgitar(false), 1000); }
      previo.current = n; setSinLeer(n);
    }).catch(() => {});
  }, [proyectoId]);

  useEffect(() => {
    contar();
    // Pestaña oculta o minimizada: no consulta (vuelve a hacerlo al volver a verla).
    const id = setInterval(() => { if (document.visibilityState === 'visible') contar(); }, 120000);
    const alVolver = () => { if (document.visibilityState === 'visible') contar(); };
    document.addEventListener('visibilitychange', alVolver);
    // Alerta nueva en la respuesta de un avance: se actualiza ya (una sola consulta, agrupada).
    let espera = null;
    const alAlertas = (e) => {
      const d = e.detail || {};
      if (!(d.nuevas?.length) && !(d.ultima > ultima.current)) return;
      ultima.current = Math.max(ultima.current, d.ultima || 0);
      clearTimeout(espera);
      espera = setTimeout(contar, 400);
    };
    window.addEventListener('mentoria:alertas', alAlertas);
    return () => {
      clearInterval(id); clearTimeout(espera);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('mentoria:alertas', alAlertas);
    };
  }, [contar]);

  // Al abrir: cargar el panel y, un momento después, marcar todo como leído
  // (así alcanzan a verse cuáles eran nuevas).
  useEffect(() => {
    if (!abierta) return undefined;
    let vivo = true;
    alertasService.panel(proyectoId).then(r => {
      if (!vivo) return;
      setAlertas(r.data?.alertas || []);
      if (r.data?.sin_leer) setTimeout(() => {
        alertasService.leer(proyectoId).then(() => { previo.current = 0; setSinLeer(0); }).catch(() => {});
      }, 1500);
    }).catch(() => vivo && setAlertas([]));
    return () => { vivo = false; };
  }, [abierta, proyectoId]);

  // Cerrar con clic afuera o Escape.
  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = (e) => {
      if (caja.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setAbierta(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setAbierta(false); };
    document.addEventListener('mousedown', fuera); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc); };
  }, [abierta]);

  const quitar = (e, id) => {
    e.stopPropagation();
    setAlertas(a => a.filter(x => x.id !== id));
    alertasService.quitar(proyectoId, id).then(r => setSinLeer(r.data?.sin_leer || 0)).catch(() => {});
  };
  // Las fijadas se quedan: el panel conserva solo esas.
  const limpiar = () => {
    setAlertas(a => (a || []).filter(x => x.fijada));
    alertasService.limpiar(proyectoId).then(r => { setSinLeer(r.data?.sin_leer || 0); previo.current = r.data?.sin_leer || 0; }).catch(() => {});
  };
  const cerrarDestacada = () => {
    if (!destacada) return;
    try { sessionStorage.setItem('alerta_destacada_vista', String(destacada.id)); } catch (e) { /* sin almacenamiento */ }
    alertasService.leer(proyectoId, [destacada.id]).then(r => setSinLeer(r.data?.sin_leer || 0)).catch(() => {});
    setDestacada(null);
  };
  const ir = (a) => {
    if (!a.enlace) return;
    alertasService.clic(proyectoId, a.id).catch(() => {});
    setAbierta(false);
    if (esExterno(a.enlace)) window.open(a.enlace, '_blank', 'noopener');
    else navigate(a.enlace.replace(window.location.origin, ''));
  };

  const B = Boton || BotonSimple;
  const etiqueta = sinLeer ? `Alertas: ${sinLeer} sin leer` : 'Alertas';
  return (
    <Caja ref={caja}>
      <B type="button" $color={color} $acento={acento} aria-label={etiqueta} aria-haspopup="dialog" aria-expanded={abierta}
        data-tip="Alertas" onClick={() => setAbierta(v => !v)} {...botonProps}>
        <Agita $on={agitar}><IcoCampana /></Agita>
        {sinLeer > 0 && <Contador $acento={acento} aria-hidden="true">{sinLeer > 99 ? '99+' : sinLeer}</Contador>}
      </B>
      {abierta && (movil ? createPortal : (x) => x)((
        <Panel ref={panelRef} $acento={acento} $fuente={movil && caja.current ? window.getComputedStyle(caja.current).fontFamily : null} role="dialog" aria-label="Alertas">
          <div className="cab">
            <b>Alertas{sinLeer > 0 && <small>{sinLeer} {sinLeer === 1 ? 'nueva' : 'nuevas'}</small>}</b>
            <Texto as={Link} to="/alertas" $acento={acento} onClick={() => setAbierta(false)}>Ver historial</Texto>
          </div>
          <div className="lista">
            {alertas === null ? <div className="vacio">Cargando…</div>
              : !alertas.length ? (
                <div className="vacio"><IcoCampana /><div>No tienes alertas en el panel.</div>
                  <div style={{ marginTop: 4, fontSize: '.8rem' }}>Las anteriores siguen en el historial.</div></div>
              ) : alertas.map(a => {
                const t = tipoDe(a.tipo);
                return (
                  <Item key={a.id} $c={t.color} $acento={acento} $nueva={!a.leida} $fija={a.fijada} $link={!!a.enlace} onClick={() => ir(a)}
                    role={a.enlace ? 'link' : undefined} tabIndex={a.enlace ? 0 : undefined}
                    onKeyDown={e => { if (e.key === 'Enter' && a.enlace) ir(a); }}>
                    {!a.leida && <span className="punto" aria-label="Nueva" />}
                    <span className="ico">{t.icono}</span>
                    <div>
                      <div className="tit">{a.titulo}</div>
                      {a.mensaje && <div className="msg">{a.mensaje}</div>}
                      <div className="meta">{a.fijada && <span className="fija"><IcoPin />Fijada</span>}<span>{hace(a.fecha)}</span>{a.enlace && <span className="ir">{a.texto_enlace || 'Ver'}</span>}</div>
                    </div>
                    {!a.fijada && <button className="x" type="button" aria-label="Quitar del panel (queda en el historial)" title="Quitar del panel" onClick={e => quitar(e, a.id)}>×</button>}
                  </Item>
                );
              })}
          </div>
          <div className="pie">
            <Texto type="button" $acento={acento} onClick={limpiar} disabled={!alertas?.some(x => !x.fijada)} title="Vacía el panel (menos las fijadas); todo queda en el historial">Limpiar panel</Texto>
            <Texto as={Link} to="/alertas" $c={acento} $acento={acento} onClick={() => setAbierta(false)}>Todas mis alertas</Texto>
          </div>
        </Panel>
      ), document.body)}
      {destacada && createPortal((
        <FondoDestacada onClick={cerrarDestacada}>
          <VentanaDestacada $acento={acento} $c={tipoDe(destacada.tipo).color} role="dialog" aria-modal="true" aria-labelledby="alerta-destacada-titulo" onClick={e => e.stopPropagation()}>
            <span className="ico">{tipoDe(destacada.tipo).icono}</span>
            <h2 id="alerta-destacada-titulo">{destacada.titulo}</h2>
            {destacada.mensaje && <p>{destacada.mensaje}</p>}
            <div className="acc">
              {destacada.enlace && <button type="button" className="si" onClick={() => { const a = destacada; cerrarDestacada(); ir(a); }}>{destacada.texto_enlace || 'Ver'}</button>}
              <button type="button" className="no" autoFocus onClick={cerrarDestacada}>{destacada.enlace ? 'Ahora no' : 'Entendido'}</button>
            </div>
          </VentanaDestacada>
        </FondoDestacada>
      ), document.body)}
    </Caja>
  );
};

export default CampanaAlertas;
